import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Search,
    Sparkles,
    Users,
    Bookmark,
    BookmarkCheck,
    Trash2,
    Download,
    ExternalLink,
    Loader2,
    XCircle,
    Filter,
    Mail,
    ShieldCheck,
    History,
    ChevronRight,
    Plus,
    X as XIcon,
    Target,
    Coins,
    AlertCircle,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CreditUsageWidget from "@/components/CreditUsageWidget";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";

const INDUSTRIES = [
    "SaaS",
    "E-commerce",
    "Real Estate",
    "Marketing Agency",
    "Finance",
    "Healthcare",
    "Manufacturing",
    "Education",
    "Media",
    "Consulting",
    "Logistics",
    "Retail",
];

const SIZES = [
    { key: "1-10", label: "1–10" },
    { key: "11-50", label: "11–50" },
    { key: "51-200", label: "51–200" },
    { key: "201-1000", label: "201–1000" },
    { key: "1000+", label: "1000+" },
];

const BATCH_OPTIONS = [10, 25, 50, 100];
const CREDITS_PER_LEAD = 1;

/* ---------- Small UI helpers ---------- */

function TagInput({ id, label, values, onChange, placeholder }) {
    const [text, setText] = useState("");
    const add = (v) => {
        const cleaned = v.trim();
        if (!cleaned || values.includes(cleaned)) return;
        onChange([...values, cleaned]);
        setText("");
    };
    const remove = (v) => onChange(values.filter((x) => x !== v));

    return (
        <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">
                {label}
            </label>
            <div className="flex flex-wrap gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 focus-within:border-[var(--color-primary)]/60 transition-colors">
                {values.map((v) => (
                    <span
                        key={v}
                        data-testid={`${id}-tag-${v}`}
                        className="inline-flex items-center gap-1 rounded-full bg-[var(--color-panel-ai)] px-2 py-0.5 text-xs text-text-main border border-[var(--color-border)]"
                    >
                        {v}
                        <button
                            aria-label={`Remove ${v}`}
                            onClick={() => remove(v)}
                            className="text-text-secondary hover:text-[var(--color-alert)]"
                        >
                            <XIcon className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                <input
                    data-testid={`${id}-input`}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            add(text);
                        } else if (e.key === "Backspace" && !text && values.length) {
                            remove(values[values.length - 1]);
                        }
                    }}
                    onBlur={() => text && add(text)}
                    placeholder={placeholder}
                    className="min-w-[110px] flex-1 bg-transparent text-sm text-text-main placeholder:text-text-secondary/60 focus:outline-none"
                />
            </div>
        </div>
    );
}

function ScoreBadge({ value }) {
    const v = Math.max(0, Math.min(100, value || 0));
    const tone =
        v >= 75
            ? "text-[var(--color-success)] border-[var(--color-success)]/40 bg-[var(--color-success)]/10"
            : v >= 50
              ? "text-[var(--color-secondary)] border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10"
              : "text-text-secondary border-[var(--color-border)] bg-[var(--color-panel-ai)]";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs tabular-nums ${tone}`}
        >
            {v}
        </span>
    );
}

function MockBadge() {
    return (
        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[var(--color-secondary)] border border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/5">
            Mock
        </span>
    );
}

function ConfirmModal({ open, onClose, onConfirm, title, message }) {
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.15 }}
                        className="fixed inset-0 z-50 grid place-items-center px-6"
                        data-testid="confirm-modal"
                    >
                        <div className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-2xl">
                            <h3 className="text-lg font-semibold">{title}</h3>
                            <p className="mt-2 text-sm text-text-secondary">
                                {message}
                            </p>
                            <div className="mt-6 flex justify-end gap-2">
                                <button
                                    onClick={onClose}
                                    data-testid="confirm-cancel"
                                    className="rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-text-secondary hover:text-text-main transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onConfirm}
                                    data-testid="confirm-ok"
                                    className="rounded-full bg-[var(--color-alert)] px-4 py-2 text-sm font-medium text-white hover:opacity-95 transition-opacity"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

/* ---------- Main page ---------- */

export default function Leads() {
    const { user, refresh } = useAuth();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // ICP form state
    const [industry, setIndustry] = useState([]);
    const [sizeRange, setSizeRange] = useState([]);
    const [location, setLocation] = useState([]);
    const [jobTitles, setJobTitles] = useState([]);
    const [keywords, setKeywords] = useState([]);
    const [batchSize, setBatchSize] = useState(10);

    // Runtime state
    const [running, setRunning] = useState(false);
    const [stage, setStage] = useState("");
    const [results, setResults] = useState([]);
    const [currentSearch, setCurrentSearch] = useState(null);
    const [historyItems, setHistoryItems] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [historyPanelOpen, setHistoryPanelOpen] = useState(true);
    const [usageBump, setUsageBump] = useState(0);

    // Filter state
    const [minScore, setMinScore] = useState(0);
    const [emailOnly, setEmailOnly] = useState(false);
    const [nameFilter, setNameFilter] = useState("");

    const estimatedCost = batchSize * CREDITS_PER_LEAD;
    const insufficient = (user?.credits ?? 0) < estimatedCost;

    const loadHistory = useCallback(async () => {
        try {
            setHistoryLoading(true);
            const data = await api.leadsSearches(20, 0);
            setHistoryItems(data.items || []);
        } catch (e) {
            toast.error(e.message || "History unavailable");
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const toggleIndustry = (name) =>
        setIndustry((cur) =>
            cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name],
        );
    const toggleSize = (key) =>
        setSizeRange((cur) =>
            cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key],
        );

    const runSearch = async () => {
        if (running || insufficient) return;
        setRunning(true);
        setResults([]);
        setCurrentSearch(null);
        setStage("Preparing ICP…");
        try {
            // Progress choreography for UX only — server does the real work.
            const stages = [
                "Fetching leads from Apollo…",
                "Enriching emails with Hunter…",
                "AI scoring vs ICP…",
            ];
            let idx = 0;
            const interval = setInterval(() => {
                idx = (idx + 1) % stages.length;
                setStage(stages[idx]);
            }, 900);

            const res = await api.leadsSearch(
                {
                    industry,
                    company_size_range: sizeRange,
                    location,
                    job_titles: jobTitles,
                    keywords,
                },
                batchSize,
            );
            clearInterval(interval);
            setResults(res.leads || []);
            setCurrentSearch({
                id: res.search_id,
                results_count: res.results_count,
                credits_spent: res.credits_spent,
                provider_used: res.provider_used,
            });
            toast.success(
                `${res.results_count} leads · ${res.credits_spent} credits used`,
            );
            await refresh();
            setUsageBump((n) => n + 1);
            loadHistory();
        } catch (e) {
            if (e.status === 429) {
                const retry = e.data?.detail?.retry_after ?? 3600;
                toast.error(
                    `Hourly credit cap reached. Retry in ~${Math.ceil(retry / 60)} min.`,
                );
            } else if (e.status === 402) {
                toast.error("Insufficient credits");
            } else {
                toast.error(e.message || "Lead search failed");
            }
        } finally {
            setRunning(false);
            setStage("");
        }
    };

    const openSearch = async (id) => {
        try {
            const data = await api.leadsSearchDetail(id);
            setResults(data.leads || []);
            setCurrentSearch({
                id,
                results_count: data.leads?.length || 0,
                credits_spent: data.search?.credits_spent || 0,
                provider_used: data.search?.provider_used,
            });
        } catch (e) {
            toast.error(e.message || "Unable to load search");
        }
    };

    const toggleSave = async (lead) => {
        try {
            const res = await api.leadsPatch(lead.id, { saved: !lead.saved });
            setResults((cur) =>
                cur.map((l) => (l.id === lead.id ? res.lead : l)),
            );
        } catch (e) {
            toast.error(e.message || "Unable to save lead");
        }
    };

    const removeFromResults = (leadId) =>
        setResults((cur) => cur.filter((l) => l.id !== leadId));

    const deleteSearch = async (id) => {
        try {
            await api.leadsSearchDelete(id);
            toast.success("Search deleted");
            setConfirmDeleteId(null);
            if (currentSearch?.id === id) {
                setCurrentSearch(null);
                setResults([]);
            }
            loadHistory();
        } catch (e) {
            toast.error(e.message || "Delete failed");
        }
    };

    const filteredResults = useMemo(() => {
        return results.filter((l) => {
            if ((l.ai_score || 0) < minScore) return false;
            if (emailOnly && !l.email_verified) return false;
            if (
                nameFilter &&
                !(
                    (l.full_name || "")
                        .toLowerCase()
                        .includes(nameFilter.toLowerCase()) ||
                    (l.company || "")
                        .toLowerCase()
                        .includes(nameFilter.toLowerCase())
                )
            )
                return false;
            return true;
        });
    }, [results, minScore, emailOnly, nameFilter]);

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed((c) => !c)}
            />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main
                    data-testid="leads-main"
                    className="flex-1 min-w-0 px-4 md:px-6 py-6"
                >
                    {/* Page header */}
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                                Lead Engine
                            </p>
                            <h1 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight">
                                Trova i tuoi{" "}
                                <span className="gradient-text">prossimi clienti</span>
                            </h1>
                            <p className="mt-1 text-sm text-text-secondary max-w-2xl">
                                Definisci l'ICP, il motore chiama Apollo + Hunter,
                                e l'AI classifica ogni lead 0–100.
                            </p>
                        </div>
                        <CreditUsageWidget refreshKey={usageBump} />
                    </div>

                    {/* 3-panel layout */}
                    <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr_300px] gap-4">
                        {/* Left — ICP form */}
                        <aside
                            data-testid="icp-form"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 h-fit"
                        >
                            <div className="flex items-center gap-2 mb-4">
                                <span className="h-8 w-8 rounded-lg bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                    <Target className="h-4 w-4 text-[var(--color-secondary)]" />
                                </span>
                                <div>
                                    <p className="text-sm font-medium">
                                        Ideal Customer Profile
                                    </p>
                                    <p className="text-xs text-text-secondary">
                                        Multi-select · tag input
                                    </p>
                                </div>
                            </div>

                            {/* Industry */}
                            <div className="space-y-1.5">
                                <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">
                                    Industry
                                </label>
                                <div className="flex flex-wrap gap-1.5">
                                    {INDUSTRIES.map((i) => {
                                        const on = industry.includes(i);
                                        return (
                                            <button
                                                key={i}
                                                data-testid={`icp-industry-${i}`}
                                                onClick={() => toggleIndustry(i)}
                                                className={`rounded-full px-2.5 py-1 text-xs transition-colors border ${
                                                    on
                                                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/15 text-text-main"
                                                        : "border-[var(--color-border)] bg-[var(--color-bg)] text-text-secondary hover:text-text-main"
                                                }`}
                                            >
                                                {i}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Company size */}
                            <div className="mt-4 space-y-1.5">
                                <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">
                                    Company size
                                </label>
                                <div className="flex flex-wrap gap-1.5">
                                    {SIZES.map((s) => {
                                        const on = sizeRange.includes(s.key);
                                        return (
                                            <button
                                                key={s.key}
                                                data-testid={`icp-size-${s.key}`}
                                                onClick={() => toggleSize(s.key)}
                                                className={`rounded-full px-2.5 py-1 text-xs transition-colors border ${
                                                    on
                                                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/15 text-text-main"
                                                        : "border-[var(--color-border)] bg-[var(--color-bg)] text-text-secondary hover:text-text-main"
                                                }`}
                                            >
                                                {s.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="mt-4">
                                <TagInput
                                    id="icp-location"
                                    label="Location"
                                    values={location}
                                    onChange={setLocation}
                                    placeholder="Italy, Milan…"
                                />
                            </div>
                            <div className="mt-4">
                                <TagInput
                                    id="icp-titles"
                                    label="Job titles"
                                    values={jobTitles}
                                    onChange={setJobTitles}
                                    placeholder="CEO, VP Sales…"
                                />
                            </div>
                            <div className="mt-4">
                                <TagInput
                                    id="icp-keywords"
                                    label="Keywords (optional)"
                                    values={keywords}
                                    onChange={setKeywords}
                                    placeholder="B2B, outbound…"
                                />
                            </div>

                            {/* Batch */}
                            <div className="mt-4 space-y-1.5">
                                <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">
                                    Batch size
                                </label>
                                <div className="grid grid-cols-4 gap-1.5">
                                    {BATCH_OPTIONS.map((b) => (
                                        <button
                                            key={b}
                                            data-testid={`icp-batch-${b}`}
                                            onClick={() => setBatchSize(b)}
                                            className={`rounded-lg px-2 py-1.5 text-sm tabular-nums transition-colors border ${
                                                batchSize === b
                                                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/15 text-text-main"
                                                    : "border-[var(--color-border)] bg-[var(--color-bg)] text-text-secondary hover:text-text-main"
                                            }`}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Cost + submit */}
                            <div className="mt-5 rounded-xl bg-[var(--color-panel-ai)] border border-[var(--color-border)] px-3 py-2.5 flex items-center justify-between">
                                <span className="text-xs text-text-secondary">
                                    Estimated cost
                                </span>
                                <span
                                    data-testid="icp-estimated-cost"
                                    className="inline-flex items-center gap-1.5 text-sm font-medium tabular-nums"
                                >
                                    <Coins className="h-3.5 w-3.5 text-[var(--color-secondary)]" />
                                    {estimatedCost} credits
                                </span>
                            </div>
                            {insufficient && (
                                <p
                                    data-testid="icp-insufficient"
                                    className="mt-2 text-xs text-[var(--color-alert)] inline-flex items-center gap-1"
                                >
                                    <AlertCircle className="h-3 w-3" />
                                    Insufficient credits ({user.credits ?? 0} available)
                                </p>
                            )}

                            <button
                                data-testid="icp-search-btn"
                                onClick={runSearch}
                                disabled={running || insufficient}
                                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {running ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Working…
                                    </>
                                ) : (
                                    <>
                                        <Search className="h-4 w-4" />
                                        Search Leads
                                    </>
                                )}
                            </button>
                        </aside>

                        {/* Center — Results */}
                        <section
                            data-testid="leads-results"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden min-h-[400px]"
                        >
                            {/* Toolbar */}
                            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-[var(--color-border)]">
                                <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-text-secondary" />
                                    <span className="text-sm font-medium">
                                        Results{" "}
                                        <span
                                            data-testid="leads-results-count"
                                            className="text-text-secondary tabular-nums"
                                        >
                                            ({filteredResults.length}
                                            {results.length !== filteredResults.length
                                                ? ` / ${results.length}`
                                                : ""}
                                            )
                                        </span>
                                    </span>
                                    {currentSearch?.provider_used === "mock" && (
                                        <MockBadge />
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="inline-flex items-center gap-1 text-xs text-text-secondary">
                                        <Filter className="h-3.5 w-3.5" />
                                        <input
                                            data-testid="leads-filter-name"
                                            value={nameFilter}
                                            onChange={(e) =>
                                                setNameFilter(e.target.value)
                                            }
                                            placeholder="Filter name/company"
                                            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs w-40 focus:outline-none focus:border-[var(--color-primary)]/50"
                                        />
                                    </div>
                                    <label className="inline-flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer">
                                        <input
                                            data-testid="leads-filter-email-verified"
                                            type="checkbox"
                                            checked={emailOnly}
                                            onChange={(e) =>
                                                setEmailOnly(e.target.checked)
                                            }
                                            className="accent-[var(--color-primary)]"
                                        />
                                        Verified email
                                    </label>
                                    <select
                                        data-testid="leads-filter-min-score"
                                        value={minScore}
                                        onChange={(e) =>
                                            setMinScore(Number(e.target.value))
                                        }
                                        className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs focus:outline-none"
                                    >
                                        <option value={0}>score ≥ 0</option>
                                        <option value={50}>score ≥ 50</option>
                                        <option value={75}>score ≥ 75</option>
                                        <option value={90}>score ≥ 90</option>
                                    </select>
                                    <a
                                        data-testid="leads-export-csv"
                                        href={
                                            currentSearch
                                                ? api.leadsExportCsvUrl(currentSearch.id)
                                                : "#"
                                        }
                                        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                                            currentSearch
                                                ? "border-[var(--color-border)] text-text-secondary hover:text-text-main hover:border-[var(--color-primary)]/50"
                                                : "border-[var(--color-border)] text-text-secondary/40 pointer-events-none"
                                        }`}
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                        Export CSV
                                    </a>
                                </div>
                            </div>

                            {/* Table / loading / empty */}
                            {running && results.length === 0 ? (
                                <div className="p-8">
                                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                                        <Loader2 className="h-4 w-4 animate-spin text-[var(--color-secondary)]" />
                                        <span data-testid="leads-stage">
                                            {stage || "Working…"}
                                        </span>
                                    </div>
                                    <div className="mt-6 space-y-2">
                                        {Array.from({ length: 6 }).map((_, i) => (
                                            <div
                                                key={i}
                                                className="h-11 rounded-lg bg-[var(--color-panel-ai)] animate-pulse-soft"
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : filteredResults.length === 0 ? (
                                <div
                                    data-testid="leads-empty"
                                    className="p-12 text-center"
                                >
                                    <div className="mx-auto h-14 w-14 rounded-full bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                        <Sparkles className="h-5 w-5 text-[var(--color-secondary)]" />
                                    </div>
                                    <h3 className="mt-4 text-base font-semibold">
                                        {results.length === 0
                                            ? "Nessuna ricerca ancora"
                                            : "Nessun risultato con questi filtri"}
                                    </h3>
                                    <p className="mt-2 text-sm text-text-secondary max-w-md mx-auto">
                                        {results.length === 0
                                            ? "Compila l'ICP a sinistra e clicca Search Leads. Puoi rivedere le ultime 20 ricerche a destra."
                                            : "Prova ad allentare i filtri o clicca su una ricerca dallo storico."}
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="text-[11px] uppercase tracking-widest text-text-secondary/80 border-b border-[var(--color-border)]">
                                            <tr>
                                                <th className="text-left px-4 py-2 font-normal">Name</th>
                                                <th className="text-left px-4 py-2 font-normal">Company</th>
                                                <th className="text-left px-4 py-2 font-normal">Role</th>
                                                <th className="text-left px-4 py-2 font-normal">Location</th>
                                                <th className="text-left px-4 py-2 font-normal">Email</th>
                                                <th className="text-left px-4 py-2 font-normal">LinkedIn</th>
                                                <th className="text-center px-4 py-2 font-normal">Score</th>
                                                <th className="text-right px-4 py-2 font-normal">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredResults.map((l, i) => (
                                                <motion.tr
                                                    key={l.id}
                                                    initial={{ opacity: 0, y: 2 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{
                                                        duration: 0.2,
                                                        delay: i * 0.01,
                                                    }}
                                                    data-testid={`lead-row-${i}`}
                                                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-panel-ai)]/50 transition-colors"
                                                >
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-text-main">
                                                                {l.full_name}
                                                            </span>
                                                            {l.source === "mock" && <MockBadge />}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary">
                                                        {l.company}
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary">
                                                        {l.job_title}
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary">
                                                        {l.location || "—"}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {l.email ? (
                                                            <span className="inline-flex items-center gap-1 text-xs">
                                                                <Mail className="h-3 w-3 text-text-secondary" />
                                                                {l.email}
                                                                {l.email_verified && (
                                                                    <ShieldCheck className="h-3 w-3 text-[var(--color-success)]" />
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="text-text-secondary/60 text-xs">
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {l.linkedin_url ? (
                                                            <a
                                                                href={l.linkedin_url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-1 text-xs text-[var(--color-secondary)] hover:underline"
                                                            >
                                                                Profile
                                                                <ExternalLink className="h-3 w-3" />
                                                            </a>
                                                        ) : (
                                                            <span className="text-text-secondary/60 text-xs">
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <ScoreBadge value={l.ai_score} />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <button
                                                                data-testid={`lead-save-${i}`}
                                                                onClick={() => toggleSave(l)}
                                                                title={l.saved ? "Unsave" : "Save"}
                                                                className="rounded-md p-1.5 text-text-secondary hover:text-[var(--color-secondary)] hover:bg-[var(--color-panel-ai)] transition-colors"
                                                            >
                                                                {l.saved ? (
                                                                    <BookmarkCheck className="h-4 w-4 text-[var(--color-secondary)]" />
                                                                ) : (
                                                                    <Bookmark className="h-4 w-4" />
                                                                )}
                                                            </button>
                                                            <button
                                                                data-testid={`lead-remove-${i}`}
                                                                onClick={() => removeFromResults(l.id)}
                                                                title="Remove from view"
                                                                className="rounded-md p-1.5 text-text-secondary hover:text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10 transition-colors"
                                                            >
                                                                <XCircle className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        {/* Right — History */}
                        <aside
                            data-testid="leads-history"
                            className={`rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 h-fit ${
                                historyPanelOpen ? "" : "xl:w-[52px] xl:overflow-hidden"
                            }`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <History className="h-4 w-4 text-text-secondary" />
                                    <span className="text-sm font-medium">History</span>
                                </div>
                                <button
                                    onClick={() => setHistoryPanelOpen((v) => !v)}
                                    className="text-text-secondary hover:text-text-main"
                                    data-testid="leads-history-toggle"
                                    aria-label="Toggle history panel"
                                >
                                    <ChevronRight
                                        className={`h-4 w-4 transition-transform ${
                                            historyPanelOpen ? "rotate-180" : ""
                                        }`}
                                    />
                                </button>
                            </div>
                            {historyPanelOpen && (
                                <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                                    {historyLoading ? (
                                        <div className="text-xs text-text-secondary">
                                            Loading…
                                        </div>
                                    ) : historyItems.length === 0 ? (
                                        <div className="text-xs text-text-secondary">
                                            No searches yet.
                                        </div>
                                    ) : (
                                        historyItems.map((it, i) => {
                                            const icp = it.icp_input || {};
                                            const summaryParts = [
                                                ...(icp.industry || []),
                                                ...(icp.job_titles || []).slice(0, 2),
                                                ...(icp.location || []).slice(0, 1),
                                            ];
                                            const summary =
                                                summaryParts.slice(0, 3).join(" · ") ||
                                                (icp.keywords?.length
                                                    ? icp.keywords.slice(0, 3).join(" · ")
                                                    : "Any ICP");
                                            const dt = new Date(it.created_at);
                                            const isActive = currentSearch?.id === it.id;
                                            return (
                                                <div
                                                    key={it.id}
                                                    data-testid={`history-item-${i}`}
                                                    className={`group relative rounded-xl border px-3 py-2.5 transition-colors ${
                                                        isActive
                                                            ? "border-[var(--color-primary)]/60 bg-[var(--color-panel-ai)]"
                                                            : "border-[var(--color-border)] bg-[var(--color-bg)] hover:border-[var(--color-primary)]/40"
                                                    }`}
                                                >
                                                    <button
                                                        onClick={() => openSearch(it.id)}
                                                        className="w-full text-left"
                                                        data-testid={`history-open-${i}`}
                                                    >
                                                        <p className="text-sm font-medium truncate">
                                                            {summary}
                                                        </p>
                                                        <p className="mt-1 text-[11px] text-text-secondary flex items-center gap-2">
                                                            <span>{dt.toLocaleString()}</span>
                                                            <span>·</span>
                                                            <span>{it.results_count} leads</span>
                                                            <span>·</span>
                                                            <span className="inline-flex items-center gap-0.5">
                                                                <Coins className="h-2.5 w-2.5" />
                                                                {it.credits_spent}
                                                            </span>
                                                        </p>
                                                        {it.status === "failed" && (
                                                            <span className="mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-[var(--color-alert)] border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/5">
                                                                failed
                                                            </span>
                                                        )}
                                                    </button>
                                                    <button
                                                        data-testid={`history-delete-${i}`}
                                                        onClick={() =>
                                                            setConfirmDeleteId(it.id)
                                                        }
                                                        aria-label="Delete search"
                                                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-text-secondary hover:text-[var(--color-alert)] transition-opacity"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </aside>
                    </div>
                </main>
            </div>

            <ConfirmModal
                open={!!confirmDeleteId}
                onClose={() => setConfirmDeleteId(null)}
                onConfirm={() => deleteSearch(confirmDeleteId)}
                title="Delete this search?"
                message="This will permanently remove the search and all its leads (GDPR hard delete). This cannot be undone."
            />
        </div>
    );
}
